import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import Refresh from '@mui/icons-material/Refresh'
import { Box, Button, Grid, Typography, Paper, Tooltip, Zoom, Checkbox } from '@mui/material'
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import React from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { type DisbursementBatchDetails } from '../../../../api-schema-types/payments/DisbursementBatchDomainDtos'
import { useGetDisbursementBatchQuery } from '../../../../api-services/payments/paymentsApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'

import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import CancelDisbursementBatch from './CancelDisbursementBatch'
import DisbursementBatchStateChip from './DisbursementBatchStateChip'
import DisbursementInstructionStateChip from './DisbursementInstructionStateChip'
import { selectUnlinkActions, setUnlinkActions, type UnlinkAction } from './disbursementsSlice'
import DownloadBatchCsv from './DownloadBatchCsv'
import ExportDisbursementBatch from './ExportDisbursementBatch'
import ProcessDisbursementBatch from './ProcessDisbursementBatch'
import UnlinkInstructions from './UnlinkInstructions'
import UnlinkFailedInstructionsComponent from '../../../components/disbursements/UnlinkFailedInstructionsComponent'

export interface BatchIdActionProps {
  disbursementBatch: DisbursementBatchDetails
}

const DisbursementBatchPage: React.FC = () => {
  const params = useParams<any>()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const unlinkActions = useAppSelector<UnlinkAction[]>(selectUnlinkActions)

  // =================================================================================================================
  //  Get Batch query

  const disbursementBatchId: number = params.disbursementBatchId
  let batch: DisbursementBatchDetails | null = null

  const getBatchApiResult = useGetDisbursementBatchQuery({ disbursementBatchId })
  const refetchBatch = getBatchApiResult.refetch

  if (getBatchApiResult.error !== null && !getBatchApiResult.isLoading && (getBatchApiResult.data != null)) {
    batch = getBatchApiResult.data
  }

  // =================================================================================================================

  // Checkbox handling !!
  const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const currentSelected = [...unlinkActions]
    const dataIndex = event.target.getAttribute('itemID')
    if (dataIndex === null) {
      console.error('No data-instruction-id attribute')
      return
    }

    const instruction = currentSelected.find((ua) => ua.instructionId === parseInt(dataIndex))
    if (instruction === undefined) {
      currentSelected.push({ instructionId: parseInt(dataIndex), checked: event.target.checked })
    } else {
      instruction.checked = event.target.checked
    }
    dispatch(setUnlinkActions(currentSelected.filter((ua) => ua.checked)))
  }

  // =================================================================================================================

  const instructionColumns: GridColDef[] = [
    { field: 'paymentInstructionId', headerName: 'ID', width: 35, editable: false },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      flex: 35,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.currencyCode}
                                              name={`[${params.row.srcCurrcurrencyCodeencyId}] ${params.row.currencyCode}`}/>
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 50,
      editable: false,
      renderCell: params => <DisbursementInstructionStateChip batchState={params.row.state}/>
    },
    { field: 'schedule', headerName: 'Schedule', flex: 40, editable: false },
    { field: 'assetHolderId', headerName: 'Member ID', flex: 50, editable: false },
    { field: 'assetHolderEmailAddress', headerName: 'Email address', flex: 80, editable: false },
    { field: 'requestAmountFormatted', headerName: 'Requested Amount', flex: 50, editable: false, align: 'right' },
    { field: 'feeAmountFormatted', headerName: 'Fee Amount', flex: 50, editable: false, align: 'right' },
    { field: 'payoutAmountFormatted', headerName: 'Payout Amount', flex: 50, editable: false, align: 'right' },
    { field: 'confirmedAtFormatted', headerName: 'Confirmed At', flex: 50, editable: false },
    { field: 'accountAddressInfo', headerName: 'Recipient', flex: 50, editable: false },
    {
      field: 'disbursementAccountId',
      headerName: 'Disbursement Account',
      flex: 50,
      editable: false,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={JSON.stringify({
            ...params.row.accountDetails,
            disbursementAccountId: params.row.disbursementAccountId
          }, null, 2)} TransitionComponent={Zoom} arrow placement={'left'}>
            <InfoIcon/>
          </Tooltip>
        )
      }
    },
    {
      field: 'unlinkInstruction',
      headerName: 'Unlink',
      flex: 20,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Checkbox value={params.row.actionUnlink} onChange={handleCheckboxClick} inputProps={{ itemID: params.row.paymentInstructionId }}/>
        )
      }
    }
  ]

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography variant={'h4'}>Disbursement Batch {batch !== null ? `${batch.disbursementBatchId} - ${batch.currencyCode}` : ''}</Typography>
        <Button variant={'outlined'} onClick={() => history.push('/app/finance-area/disbursements/batches')}
                startIcon={<ArrowBackIcon/>}>
          Disbursement Batches
        </Button>
      </Box>

      <Paper>
        <Grid container justifyContent="right" sx={{ mb: 2, pr: 4, pt: 4 }}>
          <Grid item>
            <Button variant={'contained'} type="button" onClick={refetchBatch}>
              <Refresh/>
            </Button>
          </Grid>
        </Grid>

        {getBatchApiResult.isLoading && <LoadingComponent/>}
        {getBatchApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getBatchApiResult.error)}/>}
        {getBatchApiResult.isSuccess && (
          <>
            <Box sx={{ pl: 4, pr: 4, pt: 2 }}>
              <h3>Batch Details</h3>
              <Grid container spacing={4}>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>State</strong></Typography>
                  <DisbursementBatchStateChip batchState={getBatchApiResult.data.state}/>
                </Grid>
                <Grid item md={4}>
                  <Typography variant={'body2'}><strong>Details</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.countInstructions} instructions
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    Total requested amount: {getBatchApiResult.data.totalRequestedAmountFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    Total fee amount: {getBatchApiResult.data.totalFeeAmountFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    Total payout amount: {getBatchApiResult.data.totalPayoutAmountFormatted}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>Exported</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.exportedAtFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.exportedByName}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>Processed</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.processedAtFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.processedByName}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>Cancelled</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.cancelledAtFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.cancelledByName}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ pl: 4, pr: 4, pt: 0 }}>
              <h3>Actions</h3>
              <Grid container spacing={2}>
                <Grid item md={3}>
                  <UnlinkInstructions disbursementBatch={getBatchApiResult.data}
                                      disbursementInstructionIds={unlinkActions.map((ua) => ua.instructionId)} />
                </Grid>
                <Grid item md={5} >
                    <ExportDisbursementBatch disbursementBatch={getBatchApiResult.data} />

                    <DownloadBatchCsv disbursementBatch={getBatchApiResult.data} />
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <UnlinkFailedInstructionsComponent disbursementBatch={getBatchApiResult.data}
                                                       disbursementInstructionIds={unlinkActions.map((ua) => ua.instructionId)} />
                </Grid>
                <Grid item md={2} sx={{ textAlign: 'right' }}>
                  <ProcessDisbursementBatch disbursementBatch={getBatchApiResult.data} />
                </Grid>
                <Grid item md={2} sx={{ textAlign: 'right' }}>
                  <CancelDisbursementBatch disbursementBatch={getBatchApiResult.data} />
                </Grid>
                <Grid item md={3}></Grid>
                <Grid item md={9}>
                  Filename: {getBatchApiResult.data.downloadFilename}
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ pl: 4, pr: 4, pt: 2, pb: 2 }}>
              <h3>Pending Disbursement Instructions for this batch</h3>
              <DataGrid
                getRowId={(row) => row.paymentInstructionId}
                rows={getBatchApiResult.data.instructions}
                columns={instructionColumns}
                autoHeight={true}
                disableSelectionOnClick

                pagination
                pageSize={15}
                rowsPerPageOptions={[15]}
              />
            </Box>
          </>
        )}

      </Paper>
    </>
  )
}

export default DisbursementBatchPage
